// Phase 4a — `query-knowledge-graph` push-down.
//
// `partitionFilter` splits an s-expression `:where` predicate into a
// CDF-pushable subtree (forwarded to CDF's instance-query filter language)
// and a residual subtree (applied client-side via a synthetic `filter`
// PlanNode on the produced `LazyDataset`). `translatePredicateToCdf`
// converts a pushable subtree to the CDF filter JSON shape consumed by
// `cogniteClient.instances.query()`.
//
// The s-expression `:where` is uniform with the rest of the engine —
// boolean ops + comparison/equality/string/set leaves with bare column
// symbols. CDF's filter language is a strict subset (no `!=`, no
// `is-null`, no unresolved `(col other-ds ...)` cross-dataset refs).
// Anything outside the subset routes to the residual side; the engine's
// existing `filter` verb evaluates it after the CDF round-trip.
//
// Three identity surfaces push down:
//   1. The base node properties `externalId` / `space` translate to the
//      DMS path `["node",prop]` (via the view's `property` proxy).
//   2. A direct-relation-target subpath — `(col ds relCol
//      externalId|space)` — folds to a DMS `nested` filter scoped on the
//      relation property, matching the target node by externalId/space.
//   3. A bare direct-relation column (`(in wellString [{space,
//      externalId}…])` / `(= wellString {space, externalId})`) — LHS is
//      a bare symbol whose schema type is `struct<{space, externalId}>`
//      — translates to a precise, space-aware `equals`/`in` over the
//      relation property with instance-reference value(s). This is the
//      shape produced when a cross-dataset set ref resolves to a
//      direct-relation column (`(in wellString (col other-ds rel))`).
// A relation subpath whose `nested` scope path cannot be constructed is
// a hard `PartitionInvariantError` (no client-side fallback by design).
//
// Adding a new pushable op requires two changes:
//   1. Add the op name to `CDF_PUSHABLE_LEAF_NAMES`.
//   2. Add its translator to `LEAF_TRANSLATORS`.
// The `Record<CdfPushableLeafName, ...>` typing on `LEAF_TRANSLATORS`
// keeps the two lists in sync at compile time.

import { isInstanceReference } from '#core/lib/cognite/types.ts';
import { type DataModelView, viewToSchema } from '#core/lib/cognite/view.ts';

import { type AstInput, astShape, type OpCall } from '../../normalize.ts';
import { type ColumnType, isTemporalColumn, type Schema, unwrapNullable } from '../../schema/types.ts';
import { formatDateValue } from '../transform/date-utils.ts';

/**
 * Normalize an `OpCall`'s `params` to a definite array. `OpCall.params`
 * is optional on the type because zero-arg calls omit it; every helper
 * here treats an absent `params` as an empty positional list.
 */
const callParams = (call: OpCall): readonly AstInput[] => (call.params ?? []) as readonly AstInput[];

/**
 * Pushable leaf op names. The set is intentionally narrow — only ops with
 * a direct CDF filter-language counterpart. Negations (`!=`, `not`), null
 * checks (`is-null`, `is-not-null`), and pattern ops we don't yet
 * translate route through residual.
 */
export const CDF_PUSHABLE_LEAF_NAMES = ['=', 'in', 'prefix', '<', '<=', '>', '>='] as const;
export type CdfPushableLeafName = (typeof CDF_PUSHABLE_LEAF_NAMES)[number];
const CDF_PUSHABLE_LEAF_SET: ReadonlySet<string> = new Set(CDF_PUSHABLE_LEAF_NAMES);

/**
 * Boolean combinators allowed to wrap pushable leaves. `not` is included
 * for completeness — `(not pushable-leaf)` round-trips to CDF as
 * `{ not: <leaf> }`.
 */
const CDF_PUSHABLE_BOOLEAN_NAMES: ReadonlySet<string> = new Set(['and', 'or', 'not']);

/**
 * Split an s-expression predicate into pushable + residual subtrees.
 *
 * Partitioning rules:
 *
 * - **Top-level `and`**: split children independently. Each child either
 *   goes to the pushable side or the residual side. The two sides are
 *   wrapped with `and` (or returned as-is for single children, or
 *   omitted entirely when empty).
 * - **Top-level `or` / `not` / leaf**: all-or-nothing. The subtree is
 *   pushable only if `isPushableNode` returns true for every leaf inside.
 *   Boolean logic prevents a safe partial split: e.g. `(or pushable
 *   residual)` cannot push the pushable side alone because doing so
 *   would change the truth-table.
 *
 * `schema` is the input dataset schema; column refs that name a
 * non-schema column are not pushable (they're cross-dataset refs or
 * typos — let the residual + standard column-ref diagnostics handle
 * them). `view` carries the CDF property metadata used to build the
 * DMS `nested` scope path for a direct-relation-target subpath leaf.
 */
export const partitionFilter = (
  predicate: AstInput | undefined,
  schema: Schema,
  view: DataModelView,
): { readonly pushed?: AstInput; readonly residual?: AstInput } => {
  if (predicate === undefined) {
    return {};
  }
  const shape = astShape(predicate);

  // Top-level `and`: per-child split.
  if (shape.kind === 'opCall' && shape.call.name === 'and') {
    const splits = callParams(shape.call).map((p) => partitionFilter(p as AstInput, schema, view));
    const pushedChildren = splits.flatMap((s) => (s.pushed === undefined ? [] : [s.pushed]));
    const residualChildren = splits.flatMap((s) => (s.residual === undefined ? [] : [s.residual]));
    return {
      pushed: wrapAnd(pushedChildren),
      residual: wrapAnd(residualChildren),
    };
  }

  // Otherwise: all-or-nothing.
  return isPushableNode(predicate, schema, view) ? { pushed: predicate } : { residual: predicate };
};

/**
 * Wrap `children` as `(and ...)`, return single children unwrapped, and
 * return `undefined` when empty.
 */
const wrapAnd = (children: readonly AstInput[]): AstInput | undefined => {
  if (children.length === 0) {
    return undefined;
  }
  if (children.length === 1) {
    return children[0];
  }
  return { name: 'and', params: [...children] };
};

/**
 * True iff `colType` is a direct relation as surfaced by
 * `zodTypeTagToColumnType` — a `struct` carrying both a `space` and an
 * `externalId` field. SSOT for the "is this a direct relation" test,
 * shared by `relationSubpathLeaf` (the `(col ds rel externalId|space)`
 * subpath form) and `directRelationLeaf` (the bare-symbol form).
 */
const isDirectRelationType = (colType: ColumnType | undefined): boolean =>
  colType !== undefined && colType.kind === 'struct' && 'externalId' in colType.fields && 'space' in colType.fields;

/**
 * If `lhs` is a direct-relation-target subpath reference —
 * `(col <ds> <relCol> externalId)` / `(col <ds> <relCol> space)` — and
 * `relCol` resolves on `schema` to a direct relation
 * (`struct<{space, externalId}>` per `zodTypeTagToColumnType`), return
 * the relation column name + the base subpath (`externalId` / `space`).
 * Otherwise `undefined`.
 *
 * The `(col …)` LHS is Form-3 (`(col ds col path)`); its params are
 * symbol refs (`{$sym}`). Only the two base node properties
 * (`externalId`, `space`) are pushable through a relation — any other
 * subpath has no DMS `nested` shape and routes to residual.
 */
const relationSubpathLeaf = (
  lhsNode: AstInput,
  schema: Schema,
): { readonly relColumn: string; readonly subpath: 'externalId' | 'space' } | undefined => {
  const lhs = astShape(lhsNode);
  if (lhs.kind !== 'opCall' || lhs.call.name !== 'col') {
    return undefined;
  }
  const colParams = callParams(lhs.call);
  if (colParams.length !== 3) {
    return undefined;
  }
  const relSym = astShape(colParams[1] as AstInput);
  const pathSym = astShape(colParams[2] as AstInput);
  if (relSym.kind !== 'symbolRef' || pathSym.kind !== 'symbolRef') {
    return undefined;
  }
  if (pathSym.sym !== 'externalId' && pathSym.sym !== 'space') {
    return undefined;
  }
  if (!isDirectRelationType(schema.get(relSym.sym))) {
    return undefined;
  }
  return { relColumn: relSym.sym, subpath: pathSym.sym };
};
/**
 * Boolean façade over `relationSubpathLeaf` — true iff `node` is a
 * direct-relation-target subpath reference `(col <ds> <relCol>
 * externalId|space)` whose `relCol` resolves to a direct relation on
 * `schema`.
 *
 * This is the SSOT discriminator the eager cross-dataset set-ref
 * resolver (`resolveSetRefsInPredicate`) uses to leave a queried-view
 * relation-subpath LHS untouched: such a node is NOT a cross-dataset
 * binding ref (its first arg names the queried view, not a session
 * binding) — it is push-down's job, recognised again here by
 * `isPushableNode` / `translateLeaf`. Resolving it as a binding would
 * throw a spurious `UnboundSymbolError` before push-down ever runs.
 */
export const isRelationSubpathRef = (node: AstInput, schema: Schema): boolean =>
  relationSubpathLeaf(node, schema) !== undefined;

/**
 * If `lhsNode` is a bare column symbol whose `schema` type is a direct
 * relation (`struct<{space, externalId}>` per `zodTypeTagToColumnType`),
 * return the column name; otherwise `undefined`.
 *
 * This is the bare-symbol counterpart to `relationSubpathLeaf`. It
 * recognises the intuitive `(in wellString <set-of-instance-refs>)` /
 * `(= wellString <instance-ref>)` shape — the one produced when a
 * cross-dataset set reference resolves to a direct-relation column
 * (`(in wellString (col other-ds rel))`). Such a leaf must NOT take the
 * generic scalar path: CDF rejects a direct-relation property compared
 * to a bare `externalId` string ("the value must be a json object with
 * space and externalId fields"). It pushes down as a precise,
 * space-aware `equals`/`in` over the relation property with
 * instance-reference value(s) — see `translateLeaf`.
 */
const directRelationLeaf = (lhsNode: AstInput, schema: Schema): string | undefined => {
  const lhs = astShape(lhsNode);
  if (lhs.kind !== 'symbolRef' || !isDirectRelationType(schema.get(lhs.sym))) {
    return undefined;
  }
  return lhs.sym;
};

/**
 * True iff every leaf reachable from `node` uses a pushable leaf op AND
 * names a column that exists on `schema`. Boolean combinators (`and`,
 * `or`, `not`) recurse; literal / vector / symbol nodes are leaves.
 *
 * Two pushable leaf shapes:
 *  - **Scalar**: LHS is a bare `{$sym}` column on `schema` (incl. the
 *    base node properties `externalId` / `space`, surfaced by
 *    `viewToSchema` as `stringCol`); RHS a literal / literal vector.
 *  - **Relation subpath**: LHS is `(col ds relCol externalId|space)`
 *    where `relCol` is a direct relation; RHS a literal vector. Only
 *    `=` / `in` push (they fold to a DMS `nested` filter); other ops
 *    route to residual.
 *
 * Non-pushable cases:
 * - Unknown op name (not in pushable leaf or boolean set)
 * - Leaf op with arity != 2
 * - Scalar LHS to a name not on `schema` (cross-dataset `(col other-ds
 *   col)` resolves to an op call here, not a symbol; rejected)
 */
const isPushableNode = (node: AstInput, schema: Schema, view: DataModelView): boolean => {
  const shape = astShape(node);
  if (shape.kind !== 'opCall') {
    return false;
  }
  const { name } = shape.call;
  const params = callParams(shape.call);

  // Boolean combinators: recurse over every child.
  if (CDF_PUSHABLE_BOOLEAN_NAMES.has(name)) {
    return params.every((p) => isPushableNode(p as AstInput, schema, view));
  }

  // Leaf op: must be in the leaf set, take a column ref as one arg,
  // and a literal/vector as the other. Position-strict: column on LHS
  // (convention `(op col val)`).
  if (!CDF_PUSHABLE_LEAF_SET.has(name) || params.length !== 2) {
    return false;
  }
  const lhsNode = params[0] as AstInput;
  const lhs = astShape(lhsNode);

  // Relation-subpath LHS — `(col ds relCol externalId|space)`. Only
  // `=` / `in` fold to a DMS `nested` filter; RHS must be a literal
  // vector (after the shared resolver inlined the cross-dataset set).
  const rel = relationSubpathLeaf(lhsNode, schema);
  if (rel !== undefined) {
    if (name !== 'in' && name !== '=') {
      return false;
    }
    return isLiteralOrLiteralVector(params[1] as AstInput);
  }

  // Bare direct-relation LHS — a column symbol whose schema type is
  // `struct<{space, externalId}>`. Only `=` / `in` push (they fold to
  // an instance-reference `equals`/`in` over the relation property);
  // RHS must be an instance ref or a vector of them (after the shared
  // resolver inlined the cross-dataset set). A direct relation compared
  // to a bare scalar would be a hard CDF 400 ("the value must be a json
  // object with space and externalId fields"), so it never falls
  // through to the scalar arm.
  const directRel = directRelationLeaf(lhsNode, schema);
  if (directRel !== undefined) {
    if (name !== 'in' && name !== '=') {
      return false;
    }
    return isInstanceRefOrVector(params[1] as AstInput);
  }

  // Scalar LHS — a bare column symbol on the schema.
  if (lhs.kind !== 'symbolRef') {
    return false;
  }
  if (!schema.has(lhs.sym)) {
    return false;
  }
  // RHS must be a literal (primitive, date, vector of primitives) — no
  // nested op calls, no cross-dataset refs.
  return isLiteralOrLiteralVector(params[1] as AstInput);
};

const isLiteralOrLiteralVector = (node: AstInput): boolean => {
  const shape = astShape(node);
  if (shape.kind === 'primitive' || shape.kind === 'date') {
    return true;
  }
  if (shape.kind === 'vector') {
    return shape.elements.every((el) => isLiteralOrLiteralVector(el));
  }
  return false;
};

/**
 * True iff `node` is a CDF instance reference (`{space, externalId}`)
 * or a non-empty vector of them. The RHS shape a bare direct-relation
 * leaf accepts — `astShape` classifies a `{space, externalId}` object
 * as `invalid` (it is not a primitive/vector/symbol/op-call), so
 * `isLiteralOrLiteralVector` structurally cannot match it; this
 * predicate gates the dedicated direct-relation push-down arm instead.
 * Reuses the `isInstanceReference` SSOT guard from `cognite/types.ts`.
 */
const isInstanceRefOrVector = (node: AstInput): boolean => {
  if (isInstanceReference(node)) {
    return true;
  }
  const shape = astShape(node);
  return shape.kind === 'vector' && shape.elements.length > 0 && shape.elements.every((el) => isInstanceReference(el));
};

/**
 * Translate a pushable predicate (output of `partitionFilter`'s `pushed`
 * side) to the CDF instance-query filter JSON shape.
 *
 * Caller must pass a node previously declared pushable by
 * `partitionFilter`; a non-pushable input throws `PartitionInvariantError`
 * — that's a programming error, not a runtime LLM error. The schema is
 * derived from `view` via `viewToSchema` (SSOT) so direct-relation
 * subpath leaves can be recognised for `nested`-filter translation.
 */
export const translatePredicateToCdf = (pushed: AstInput, view: DataModelView): unknown => {
  const shape = astShape(pushed);
  if (shape.kind !== 'opCall') {
    throw new PartitionInvariantError(`expected an op call, got ${shape.kind}`);
  }
  return translateCall(shape.call, view, viewToSchema(view));
};

const translateCall = (call: OpCall, view: DataModelView, schema: Schema): unknown => {
  if (CDF_PUSHABLE_BOOLEAN_NAMES.has(call.name)) {
    const children = callParams(call).map((p) => {
      const childShape = astShape(p as AstInput);
      if (childShape.kind !== 'opCall') {
        throw new PartitionInvariantError(`boolean combinator child must be an op call, got ${childShape.kind}`);
      }
      return translateCall(childShape.call, view, schema);
    });
    if (call.name === 'not') {
      if (children.length !== 1) {
        throw new PartitionInvariantError(`(not) takes exactly one child, got ${children.length}`);
      }
      return { not: children[0] };
    }
    return { [call.name]: children };
  }
  return translateLeaf(call, view, schema);
};

/**
 * Per-op translator. Each entry takes the pushable leaf call (its `params`
 * are positionally `[column-ref, literal-value]`) plus the CDF property
 * identifier for the column, and returns the CDF leaf-filter JSON.
 *
 * The base node properties `externalId` / `space` resolve through the
 * view's `property` proxy to `["node", prop]` and flow through this
 * table unchanged. Direct-relation-target subpath leaves are handled
 * before this table — they fold to a distinct DMS `nested` shape.
 *
 * `value` is the literal already extracted from the call AND
 * type-coerced by `translateLeaf` (see `coerceCdfValue`) — translators
 * never re-read `callParams`, so date/timestamp normalization is applied
 * uniformly in one place.
 */
const LEAF_TRANSLATORS: Record<CdfPushableLeafName, (value: unknown, property: unknown) => unknown> = {
  '=': (value, property) => ({ equals: { property, value } }),
  in: (value, property) => ({ in: { property, values: value } }),
  prefix: (value, property) => ({ prefix: { property, value } }),
  '>': (value, property) => ({ range: { property, gt: value } }),
  '>=': (value, property) => ({ range: { property, gte: value } }),
  '<': (value, property) => ({ range: { property, lt: value } }),
  '<=': (value, property) => ({ range: { property, lte: value } }),
};

// CDF DMS `timestamp` / `date` properties expect ISO-8601 strings. An
// LLM routinely supplies a bare date string (`"2025-01-01"`) or the
// engine a `Date` / epoch-ms; pushing that raw into the instance-query
// filter silently matches nothing (or 400s). Normalize date/timestamp
// literals to the format CDF expects; non-date columns pass through
// byte-for-byte (existing behavior preserved). Reuses the `toDateValue`
// SSOT via `formatDateValue`.
const CDF_TIMESTAMP_PATTERN = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const coerceCdfValue = (raw: unknown, colType: ColumnType | undefined): unknown => {
  if (colType === undefined || !isTemporalColumn(colType)) {
    return raw;
  }
  const pattern = unwrapNullable(colType).kind === 'date' ? 'YYYY-MM-DD' : CDF_TIMESTAMP_PATTERN;
  const norm = (v: unknown): unknown => formatDateValue(v, pattern) ?? v;
  return Array.isArray(raw) ? raw.map(norm) : norm(raw);
};

const translateLeaf = (call: OpCall, view: DataModelView, schema: Schema): unknown => {
  const lhsNode = callParams(call)[0] as AstInput;

  // Relation-subpath LHS — `(col ds relCol externalId|space)`. Folds to
  // a DMS `nested` filter: scope = the relation property's DMS path,
  // inner = an `in` over the base node property. Only `=` / `in` reach
  // here (gated by `isPushableNode`); `=` folds to a single-element
  // value list (same shape as `in`).
  const rel = relationSubpathLeaf(lhsNode, schema);
  if (rel !== undefined) {
    let scope: unknown;
    try {
      scope = (view.property as Record<string, unknown>)[rel.relColumn];
    } catch (e) {
      throw new PartitionInvariantError(
        `cannot build DMS \`nested\` scope path for relation '${rel.relColumn}' on view '${view.externalId}': ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    }
    if (scope === undefined || scope === null) {
      throw new PartitionInvariantError(
        `relation '${rel.relColumn}' is not a property of view '${view.externalId}'; cannot build \`nested\` scope`,
      );
    }
    const rawValues = callParams(call)[1];
    const values = call.name === 'in' ? (Array.isArray(rawValues) ? rawValues : []) : [rawValues];
    return {
      nested: {
        scope,
        filter: { in: { property: ['node', rel.subpath], values } },
      },
    };
  }

  // Bare direct-relation LHS — `(in wellString [{space,externalId}…])`
  // / `(= wellString {space,externalId})`. Folds to a precise,
  // space-aware `in` over the relation property with instance-reference
  // values (CDF accepts an object value list for a direct relation; a
  // single `=` value is normalised to a one-element list — `in [x]` ≡
  // `equals x`, mirroring the relation-subpath translator's value
  // normalisation). Only `=` / `in` reach here (gated by
  // `isPushableNode`).
  const directRel = directRelationLeaf(lhsNode, schema);
  if (directRel !== undefined) {
    const property = (view.property as Record<string, unknown>)[directRel];
    if (property === undefined || property === null) {
      throw new PartitionInvariantError(
        `direct relation '${directRel}' is not a property of view '${view.externalId}'`,
      );
    }
    const rawValues = callParams(call)[1];
    const values = Array.isArray(rawValues) ? rawValues : [rawValues];
    return { in: { property, values } };
  }

  const lhs = astShape(lhsNode);
  if (lhs.kind !== 'symbolRef') {
    throw new PartitionInvariantError(`leaf LHS must be a symbol ref or a relation subpath, got ${lhs.kind}`);
  }
  const column = lhs.sym;

  // Base node properties (`externalId` / `space`) and view-declared
  // properties alike resolve through the view's `property` proxy:
  // `externalId`/`space` → `["node", prop]`; declared properties →
  // `[space, table, prop]`. No special-casing needed here.
  const property = (view.property as Record<string, unknown>)[column];
  if (property === undefined || property === null) {
    throw new PartitionInvariantError(`column '${column}' is not a property of view '${view.externalId}'`);
  }

  const translator = LEAF_TRANSLATORS[call.name as CdfPushableLeafName];
  if (translator === undefined) {
    throw new PartitionInvariantError(`leaf op '${call.name}' has no CDF translator (programming error)`);
  }
  const value = coerceCdfValue(callParams(call)[1], schema.get(column));
  return translator(value, property);
};

/**
 * Programming error — the partitioner declared a node pushable but the
 * translator can't handle it. The `partitionFilter` / `translateCall`
 * contract guarantees this never fires; surface it as a hard internal
 * error rather than a structured `ExprError` so test failures are
 * obvious.
 */
export class PartitionInvariantError extends Error {
  constructor(message: string) {
    super(`pushdown invariant violation: ${message}`);
    this.name = 'PartitionInvariantError';
  }
}
